/**
 * @file helper.ts
 * @description Утилиты для работы с фильтрами в Task Manager приложении. Предоставляет функции для создания компонентов фильтров различных типов (dateRange, dictionary, select, checkbox), подготовки данных для отправки на сервер, обработки активных фильтров и управления состоянием фильтрации в таблицах и списках данных с поддержкой сохранения пользовательских настроек.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-01-16
 * @dependencies ['@components/form-controls/date-picker', '@components/form-controls/dictionary', '@components/form-controls/select', '@components/form-controls/checkbox', '@utils/date-time-helper', 'moment', '@utils/form-object-helper', '@features/filter/types', 'react']
 * @related-files ['src/components/form-controls', 'src/features/filter/types.ts', 'src/components/ui/tables', 'src/pages']
 */

import { DatePicker } from '@components/form-controls/date-picker';
import { Dictionary } from '@components/form-controls/dictionary';
import { Select } from '@components/form-controls/select';
import { Checkbox } from '@components/form-controls/checkbox';
import { getDateRangeMS } from '@utils/date-time-helper';
import moment from 'moment';
import { getSimpleObject } from '@utils/form-object-helper';
import { filterComponent } from '@features/filter/types';
import { createElement } from 'react';
import { GuidUtils } from '@src/types';

/**
 * Возвращает массив компонентов и свойств для отрисовки на форме фильтрации
 * @param filterData - данные фильтра
 * @param control - объект управления формой
 * @returns объект с компонентом и его свойствами или null
 */
export const getFilterComponent = (
    filterData: any,
    control: any
): { component: any; props: any } | null => {
    switch (filterData.filterType) {
        case 'dateRange':
            return {
                component: DatePicker,
                props: {
                    type: 'date-range',
                    control: control,
                    name: filterData.name,
                    ...filterData.filterProps
                }
            };
        case 'date':
            return {
                component: DatePicker,
                props: {
                    type: 'date',
                    control: control,
                    name: filterData.name,
                    ...filterData.filterProps
                }
            };
        case 'dictionary':
            return {
                component: Dictionary,
                props: {
                    control: control,
                    name: filterData.name,
                    inputType: 'default',
                    view: 'default',
                    ...filterData.filterProps
                }
            };
        case 'select':
            return {
                component: Select,
                props: {
                    control: control,
                    name: filterData.name,
                    ...filterData.filterProps
                }
            };
        case 'checkbox':
        case 'columnVisibility':
            return {
                component: Checkbox,
                props: {
                    control: control,
                    name: filterData.name,
                    ...filterData.filterProps
                }
            };
        default:
            return null;
    }
};

/**
 * Подготавливает данные для отправки из reactHookForm
 * @param formData - данные формы
 * @param filterList - список фильтров
 * @returns массив подготовленных для отправки фильтров
 */
export const prepareFilterFormToFetch = (formData: any, filterList: any) => {
    return Object.entries(formData).reduce((acc: any, [field, value]: any) => {
        if (!value) {
            return acc;
        }

        const currentFilter = filterList.find((filter: any) => filter.name === field);

        if (!currentFilter || ['columnsSettings', 'columnVisibility'].includes(currentFilter.filterType)) {
            return acc;
        }

        let comparison = getComparisonByFilterType(currentFilter.filterType);

        let values = [value];

        if (Array.isArray(value)) {
            values = value;
        }

        if (currentFilter.filterType === 'dictionary') {
            if (!value || !value.id || !value.$type || !value.name) {
                return acc;
            }
        }

        if (Array.isArray(value) && currentFilter.filterType === 'dateRange') {
            values = getDateRangeMS(value);

            if (!values[0] && !values[1]) {
                return acc;
            }

            if (!values[0] && values[1]) {
                comparison = 'lessThanOrEqual';
                values = [values[1]];
            } else if (!values[1] && values[0]) {
                comparison = 'greaterThanOrEqual';
                values = [values[0]];
            }
        }

        if (currentFilter.filterType === 'select') {
            if (!value || !value.id) {
                return acc;
            }
        }

        if (value && value.$type) {
            if (!value.id || value.id === import.meta.env.VITE_EMPTY_GUID) {
                return acc;
            }

            values = [value.id];
        }

        return [
            ...acc,
            {
                field,
                comparison,
                values
            }
        ];
    }, []);
};

/**
 * Возвращает тип сравнения по типу фильтра
 * @param type - тип фильтра
 * @returns строка с типом сравнения
 */
export const getComparisonByFilterType = (type: string): string => {
    switch (type) {
        case 'dateRange':
            return 'between';
        case 'dictionary':
            return 'in';
        default:
            return 'equal';
    }
};

/**
 * Возвращает массив активных фильтров из массива filterControls
 * @param filterControls - массив элементов управления фильтрами
 * @returns массив активных фильтров с отформатированными заголовками
 */
export const getActiveFilters = (filterControls: any[]): any[] => {
    return filterControls
        .filter(
            (filter) =>
                filter.tab === 'filters' &&
                filter.filterProps?.label &&
                !(filter.filterType === 'dateRange' && (!filter.value || (!filter.value[0] && !filter.value[1]))) &&
                ((filter.value?.id && filter.value.id !== import.meta.env.VITE_EMPTY_GUID) ||
                    (filter.value && typeof filter.value !== 'object' && filter.filterProps?.label) ||
                    ((filter.value || filter.linkField) &&
                        filter.filterType === 'dateTime' &&
                        filter.filterProps?.label) ||
                    (filter.value && typeof filter.value === 'object' && filter.filterType === 'dateRange') ||
                    (filter.value &&
                        Array.isArray(filter.value) &&
                        filter.value.length &&
                        filter.filterType === 'table'))
        )
        .map((filter) => {
            const name = filter.name;
            let title = `${filter.filterProps.label}: ${filter.value}`;

            if (['dictionary', 'select'].includes(filter.filterType)) {
                title = `${filter.filterProps.label}: ${filter.value?.name}`;
            }

            if (filter.filterType === 'table') {
                title = `${filter.filterProps.label}: Список значений`;
            }

            if (['switch'].includes(filter.filterType)) {
                title = filter.filterProps.label;
            }

            if (['date', 'dateTime'].includes(filter.filterType)) {
                title = `${filter.filterProps.label}: ${
                    filter.value ? moment(filter.value).format('L HH:mm') : 'Бессрочно'
                }`;

                if (filter.linkField) {
                    const endDate = filterControls.find((filterControl) => filterControl.name === filter.linkField);

                    if (endDate) {
                        if (!filter.value && !endDate.value) {
                            return null;
                        }

                        title = `${filter.filterProps.label}: ${
                            filter.value ? moment(filter.value).format('L HH:mm') : 'Бессрочно'
                        } - ${endDate.value ? moment(endDate.value).format('L HH:mm') : 'Бессрочно'}`;
                    }
                }
            }

            if (['dateRange'].includes(filter.filterType)) {
                title = `${filter.filterProps.label}: ${
                    filter.value[0] ? moment(filter.value[0]).format('L') : 'Бессрочно'
                } - ${filter.value[1] ? moment(filter.value[1]).format('L') : 'Бессрочно'}`;
            }

            return { title, name, isCanRemove: !filter.filterProps.disabled };
        })
        .filter((filter) => filter);
};

/**
 * Подготавливает контролы фильтров для отправки
 * @param filterControls - массив контролов фильтров
 * @returns подготовленные для отправки данные
 */
export const prepareFilterControlsToFetch = (filterControls: any) => {
    const preparedForm = getInitialValuesFromFilterControls(filterControls);

    return prepareFilterFormToFetch(preparedForm, filterControls);
};

/**
 * Получает начальные значения из контролов фильтров
 * @param filterControls - массив контролов фильтров
 * @returns объект с начальными значениями формы
 */
export const getInitialValuesFromFilterControls = (filterControls: any) => {
    return filterControls.reduce((acc: any, curr: any) => {
        let value = curr.value;

        if (!value) {
            switch (curr.filterType) {
                case 'dictionary':
                    value = getSimpleObject({ id: GuidUtils.empty(), name: '', $type: curr.type });
                    break;
                case 'select':
                    value = getSimpleObject({ $type: curr.type, id: GuidUtils.empty(), name: 'Все' });
                    break;
                case 'dateRange':
                    value = [undefined, undefined];
                    break;
            }
        }

        acc[curr.name] = value;

        return acc;
    }, {});
};

/**
 * Подготавливает фильтры для сохранения
 * @param filters - массив компонентов фильтров
 * @returns объект с фильтрами, пригодными для сохранения
 */
export const prepareFiltersToSave = (filters: filterComponent[]) => {
    return filters
        .filter(
            (filterControl: filterComponent) =>
                !(
                    filterControl.isNotSaved ||
                    filterControl.filterType === 'dateRange' ||
                    filterControl.value === undefined ||
                    (filterControl.value?.id === import.meta.env.VITE_EMPTY_GUID &&
                        filterControl.filterType === 'dictionary')
                )
        )
        .reduce((acc: any, curr: filterComponent) => {
            acc[curr.name] = curr.value;
            return acc;
        }, {});
};

/**
 * Получает поле фильтра по имени
 * @param filters - массив фильтров
 * @param control - объект управления формой
 * @param name - имя поля
 * @param props - дополнительные свойства
 * @returns React элемент поля фильтра или undefined
 */
export const getFieldByName = (filters: any[], control: any, name: string, props: any = {}) => {
    const element = filters.find((filter: any) => filter.name === name);

    if (!element) {
        return;
    }

    const component: any = getFilterComponent(element, control);

    if (!component) {
        return;
    }

    return createElement(component.component, { ...props, ...component.props });
};